"use client";

import { useEffect, useState, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Diamond, Zap, Wallet, Smartphone, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { A11y, Autoplay, EffectCoverflow, Pagination } from "swiper/modules";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import "swiper/css/a11y";
const features = [{
  icon: Diamond,
  title: "Professional designs",
  desc: "Corporate-standard quality"
}, {
  icon: Zap,
  title: "Fast turnaround",
  desc: "Get your flyer in no time"
}, {
  icon: Wallet,
  title: "Affordable pricing",
  desc: "Starting from just LKR 999"
}, {
  icon: Smartphone,
  title: "Optimized for social",
  desc: "Perfect for Instagram & ads"
}];
const mockups = [{
  id: 1,
  title: "Penta Technology Solutions",
  theme: "Corporate Security",
  src: "https://yasindhuravith.com/wp-content/uploads/2026/09/Chartered-Institute-Of-Procurement-Supply-Now-In-Sri-Lanka-scaled.png?v=57rspmif9s5",
  srcset: "https://yasindhuravith.com/wp-content/uploads/2026/09/Trusted-Security-Solutions-Provider-For-Sri-Lankan-Banks-scaled.png?v=c8z1zh4tmiw-wvc-srcset",
  sizes: "https://yasindhuravith.com/wp-content/uploads/2026/09/Trusted-Security-Solutions-Provider-For-Sri-Lankan-Banks-scaled.png?v=vs8cals8zle-wvc-sizes",
  alt: "Penta Technology Solutions flyer design showcasing complete security solutions",
  accentColor: "bg-zinc-900 text-zinc-100",
  badge: "Enterprise"
}, {
  id: 2,
  title: "CIPS Sri Lanka",
  theme: "Global Qualifications",
  src: "https://yasindhuravith.com/wp-content/uploads/2026/09/Chartered-Institute-Of-Procurement-And-Supply-Qualifications-Promotional-Flyer-scaled.png?v=d2bc66j6vxh",
  srcset: "https://yasindhuravith.com/wp-content/uploads/2026/09/Chartered-Institute-Of-Procurement-And-Supply-Qualifications-Promotional-Flyer-scaled.png?v=h4yw5ukzjr-wvc-srcset",
  sizes: "https://yasindhuravith.com/wp-content/uploads/2026/09/Chartered-Institute-Of-Procurement-And-Supply-Qualifications-Promotional-Flyer-scaled.png?v=lmcii2nby9-wvc-sizes",
  alt: "CIPS Sri Lanka promotional flyer featuring professional global qualifications",
  accentColor: "bg-blue-900 text-white",
  badge: "Featured Work"
}, {
  id: 3,
  title: "SpiceHub Restaurant",
  theme: "Authentic Flavours",
  src: "https://yasindhuravith.com/wp-content/uploads/2026/09/1783668211583-1.jpeg?v=3glu9b6yky6",
  srcset: "https://yasindhuravith.com/wp-content/uploads/2026/09/1783668211583-1.jpeg?v=hcwmqqu5x25-wvc-srcset",
  sizes: "https://yasindhuravith.com/wp-content/uploads/2026/09/1783668211583-1.jpeg?v=qddfrmu5v4t-wvc-sizes",
  alt: "SpiceHub restaurant dining and food takeaway promotional poster",
  accentColor: "bg-neutral-900 text-amber-100",
  badge: "Food & Beverage"
}, {
  id: 4,
  title: "Creative Summit Poster",
  theme: "Digital Marketing Event",
  src: "https://yasindhuravith.com/wp-content/uploads/2026/09/Trusted-Security-Solutions-Provider-For-Sri-Lankan-Banks-scaled.png?v=faaom5tuiyv",
  srcset: "https://yasindhuravith.com/wp-content/uploads/2026/09/Trusted-Security-Solutions-Provider-For-Sri-Lankan-Banks-scaled.png?v=ck3k6yd61ew-wvc-srcset",
  sizes: "https://yasindhuravith.com/wp-content/uploads/2026/09/Trusted-Security-Solutions-Provider-For-Sri-Lankan-Banks-scaled.png?v=2teg67j15l2-wvc-sizes",
  alt: "Creative digital summit modern corporate event promotional flyer",
  accentColor: "bg-purple-950 text-purple-50",
  badge: "Event & Promo"
}];
const stats = [{
  metric: "300+",
  label: "Flyers designed"
}, {
  metric: "100%",
  label: "Client satisfaction"
}, {
  metric: "Fast",
  label: "Delivery time"
}, {
  metric: "3+",
  label: "Trusted by corporates"
}];
export default function Flyers() {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(1);
  const shouldReduceMotion = useReducedMotion();
  return <section id="highqualityflyers" data-nav="light" className="relative overflow-hidden bg-background py-16 lg:py-24">
      {/* Ambient background glows */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 size-96 rounded-full bg-primary/10 blur-[120px]" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -right-24 size-96 rounded-full bg-secondary/10 blur-[120px]" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header Taglines */}
        <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <Badge variant="outline" className="rounded-full border-primary/20 bg-accent px-3 py-1 font-medium text-xs tracking-wider text-accent-foreground uppercase">
            <span className="size-1.5 rounded-full bg-primary" />
            Creative design services
          </Badge>

          <div className="flex items-center text-xs font-semibold tracking-widest text-muted-foreground uppercase">
            <span>Ideas</span>
            <span className="mx-2 text-border">|</span>
            <span>Designs</span>
            <span className="mx-2 text-border">|</span>
            <span className="relative text-foreground">
              Results
              <span className="absolute -bottom-1 left-0 h-0.5 w-full bg-primary" />
            </span>
          </div>
        </div>

        {/* Main 2-Column Content Grid */}
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="flex flex-col lg:col-span-6 xl:col-span-5">
            {/* Main Headline */}
            <h1 className="font-default text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              High-quality flyers{" "}
              <span className="text-primary">for your business</span>
            </h1>

            {/* Description */}
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              {"I've designed flyers for many leading corporate brands in Sri Lanka. Now, you can get the same high professional quality designs for an affordable rate, starting from just"}
            </p>

            {/* Price Callout Card */}
            <div className="relative mt-6 inline-flex max-w-sm flex-col rounded-2xl border border-primary/15 bg-accent/60 p-5 backdrop-blur-sm sm:max-w-md">
              <div className="flex items-baseline gap-2">
                <span className="font-default text-3xl font-black tracking-tight text-primary sm:text-4xl">
                  LKR 999
                </span>
                <span className="text-sm font-semibold text-muted-foreground">
                  / design
                </span>
              </div>
              <p className="mt-1 text-sm font-medium text-foreground">
                High-quality flyer design
              </p>

              {/* Decorative accent slash marks */}
              <div aria-hidden="true" className="absolute top-4 right-4 flex gap-1 opacity-70">
                <span className="h-4 w-1 -rotate-12 rounded-full bg-primary" />
                <span className="h-4 w-1 -rotate-12 rounded-full bg-primary/60" />
              </div>
            </div>

            {/* 4 Feature Items Grid */}
            <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6">
              {features.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.title} data-index={i} className="flex flex-col items-start gap-2.5 rounded-xl border border-border/40 bg-card/40 p-3 sm:p-4">
                    <div className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <IconComponent className="size-4.5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground">
                        {item.title}
                      </h3>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {item.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* CTA Button and Market Footnote */}
            <div className="mt-8 flex flex-col items-start gap-3">
              <Button asChild size="lg" className="group h-12 rounded-xl bg-gradient-to-r from-primary to-secondary px-8 font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-[transform,box-shadow] hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/30">
                <Link to="https://wa.me/94740688693?text=Hi!%20Can%20you%20make%20a%20flyer%20for%20me" target="_blank" rel="noopener noreferrer">
                  <span>Get your flyer now</span>
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <div className="relative pt-1">
                <p className="text-xs text-muted-foreground">
                  Flyer Maker by YRK — One of the best in the Sri Lankan market
                </p>
                <div className="mt-1 h-0.5 w-12 bg-primary/80" />
              </div>
            </div>
          </motion.div>

          {/* Right Column: 3D Mockup Slider */}
          <div className="relative lg:col-span-6 xl:col-span-7">
            {/* Handwritten annotation header with arrow */}
            <div className="mb-4 flex items-center justify-end pr-4 lg:pr-12">
              <div className="relative flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-primary italic sm:text-base">
                  Some of my work
                </span>
                <svg className="size-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 4c6 2 12 8 14 14" />
                  <path d="M14 18h4v-4" />
                </svg>
              </div>
            </div>

            {/* Mockup Carousel Container */}
            <div className="relative mx-auto max-w-[540px] px-2 sm:px-6">
              <Swiper modules={[Autoplay, EffectCoverflow, Pagination, A11y]} effect="coverflow" speed={900} autoplay={{
              delay: 2800,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }} grabCursor centeredSlides initialSlide={1} slidesPerView={1.2} breakpoints={{
              480: {
                slidesPerView: 1.35,
                spaceBetween: -20
              },
              640: {
                slidesPerView: 1.5,
                spaceBetween: -30
              },
              1024: {
                slidesPerView: 1.45,
                spaceBetween: -24
              }
            }} coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 140,
              modifier: 1.8,
              slideShadows: false
            }} pagination={{
              clickable: true,
              bulletClass: "inline-block size-2.5 rounded-full bg-muted-foreground/30 mx-1 cursor-pointer transition-all duration-300",
              bulletActiveClass: "!bg-primary !w-6"
            }} onSwiper={setSwiperInstance} onSlideChange={swiper => setActiveIndex(swiper.realIndex)} a11y={{
              prevSlideMessage: "Previous flyer mockup",
              nextSlideMessage: "Next flyer mockup"
            }} className="!overflow-visible py-4 pb-12">
                {mockups.map((mockup, i) => <SwiperSlide key={mockup.id} data-index={i} className="transition-all duration-300">
                    <div className={`relative mx-auto w-full max-w-[310px] max-h-[480px] overflow-hidden rounded-2xl border-4 border-background bg-card shadow-2xl transition-all duration-300 ${activeIndex === i ? "ring-4 ring-primary/20 shadow-primary/20 scale-100" : "opacity-80 scale-95"}`}>
                      <img src={mockup.src} data-wvc-srcset={mockup.srcset} data-wvc-sizes={mockup.sizes} alt={mockup.alt} loading="lazy" className="h-auto max-h-[480px] w-auto max-w-full object-contain mx-auto block" />
                    </div>
                  </SwiperSlide>)}
              </Swiper>

              {/* Custom Navigation Arrows */}
              <div className="absolute top-1/2 left-0 z-20 -translate-y-1/2">
                <Button variant="outline" size="icon" aria-label="Previous flyer" onClick={() => swiperInstance?.slidePrev()} className="size-10 rounded-full border-border bg-background/95 shadow-md backdrop-blur-sm hover:bg-accent hover:text-accent-foreground">
                  <ChevronLeft className="size-5" />
                </Button>
              </div>

              <div className="absolute top-1/2 right-0 z-20 -translate-y-1/2">
                <Button variant="outline" size="icon" aria-label="Next flyer" onClick={() => swiperInstance?.slideNext()} className="size-10 rounded-full border-border bg-background/95 shadow-md backdrop-blur-sm hover:bg-accent hover:text-accent-foreground">
                  <ChevronRight className="size-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Stats Bar */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-14 rounded-2xl border border-primary/15 bg-card/85 p-6 shadow-xl backdrop-blur-md lg:mt-16">
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4 sm:gap-4">
            {stats.map((stat, i) => <div key={stat.label} data-index={i} className="relative flex flex-col items-center justify-center text-center sm:first:border-none">
                <span className="font-default text-3xl font-extrabold tracking-tight text-primary sm:text-4xl">
                  {stat.metric}
                </span>
                <span className="mt-1 text-xs font-medium text-muted-foreground sm:text-sm">
                  {stat.label}
                </span>

                {/* Vertical Separators between stats on larger screens */}
                {i < stats.length - 1 && <Separator orientation="vertical" className="absolute -right-2 top-2 hidden h-10 bg-border/80 sm:block lg:-right-4" />}
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}